import React from "react"
import { useTranslation } from "react-i18next"

import { CopyIconButton } from "@/components/copy-icon-button"
import type { Organization } from "@/models/organization"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { useUtilityCredentialSecret } from "@/services/credential"

import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"

/** Mask shown before the secret is fetched; not the real password. */
const MASKED_PASSWORD_PLACEHOLDER = "••••••••••••"

interface CredentialPasswordRevealFieldProps {
  credentialId: string
  organizationId: Organization["id"]
}

const passwordDisplaySx = (isShowingPassword: boolean) =>
  isShowingPassword
    ? { fontFamily: "inherit", letterSpacing: "normal" }
    : { fontFamily: "monospace", letterSpacing: "0.12em" }

export const CredentialPasswordRevealField: React.FC<
  CredentialPasswordRevealFieldProps
> = ({ credentialId, organizationId }) => {
  const { t } = useTranslation("common")
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false)
  const inflightSecretRef = React.useRef<Promise<string> | null>(null)

  const {
    utilityCredentialSecretData,
    utilityCredentialSecretIsPending,
    utilityCredentialSecretMutateAsync,
  } = useUtilityCredentialSecret()

  const revealedPassword = utilityCredentialSecretData?.password ?? null
  const isShowingPassword = isPasswordVisible && revealedPassword != null
  const displayValue = isShowingPassword
    ? revealedPassword
    : MASKED_PASSWORD_PLACEHOLDER

  const ensureSecret = (): Promise<string> => {
    if (revealedPassword != null) {
      return Promise.resolve(revealedPassword)
    }

    inflightSecretRef.current ??= utilityCredentialSecretMutateAsync({
      credentialId,
      organizationId,
    })
      .then((secret) => secret.password)
      .finally(() => {
        inflightSecretRef.current = null
      })

    return inflightSecretRef.current
  }

  const handleToggleVisibility = () => {
    if (isPasswordVisible) {
      setIsPasswordVisible(false)
      return
    }

    void ensureSecret()
      .then(() => {
        setIsPasswordVisible(true)
      })
      .catch(() => undefined)
  }

  return (
    <ReadonlyField
      label={t("fields.password.title")}
      value={
        <Stack
          direction="row"
          sx={{ alignItems: "center", gap: 0.5, minHeight: 40 }}
        >
          <Box
            aria-label={t("fields.password.title")}
            component="span"
            sx={passwordDisplaySx(isShowingPassword)}
          >
            {displayValue}
          </Box>
          <CopyIconButton
            ariaLabel={t("admin.credentialsUi.copyPasswordAriaLabel")}
            disabled={utilityCredentialSecretIsPending}
            resolveValue={ensureSecret}
          />
          <IconButton
            aria-label={t("button.togglePasswordVisibility")}
            disabled={utilityCredentialSecretIsPending}
            onClick={handleToggleVisibility}
          >
            {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Stack>
      }
    />
  )
}
